// Lời gọi API "Tra cứu KC" (hp-media-server-staging/src/kc-lookup, docs/tra-cuu-kc/00-nghien-cuu-2026-10-27.md).
// Lỗi nghiệp vụ là 409 `{ code, message }` (KC_LOOKUP_NOT_ALLOWED…), sai dữ liệu là 400 `{ message }`.

import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { parseFilename, triggerDownload } from 'pages/attendance-manager/component/export-all-modal';

export type LookupMode = 'UNION' | 'INTERSECT';
export type CompareMode = 'NONE' | 'PREV' | 'SAME_DAYS';
export type SegmentSource = 'MONTH_FILE' | 'DAILY' | 'LEGACY' | 'MIXED' | 'NONE';
export type TokenStatus =
  | 'OK'
  | 'RENAMED'
  | 'DUPLICATE'
  | 'NOT_FOUND'
  | 'INVALID'
  | 'NO_DATA'
  | 'OTHER_EMAIL'
  | 'OUT_OF_SCOPE';
export type MonthStatusCode =
  | 'FUTURE'
  | 'RUNNING'
  | 'MONTH_FILE'
  | 'NO_DATA'
  | 'LEGACY'
  | 'DAILY_INCOMPLETE'
  | 'DAILY_COMPLETE';

export interface MonthInfo {
  month: string;
  status: MonthStatusCode;
  totalDays: number;
  dueDays: number;
  dayFiles: number;
  monthFile: boolean;
  missingDays: string[];
}

export interface AgentInfo {
  email: string;
  creators: number;
  diamonds: number;
  groups: string;
}

export interface GroupInfo {
  /** Cột D "Nhóm" của file TikTok, giữ nguyên chữ. */
  name: string;
  creators: number;
  diamonds: number;
  emails: string;
}

export interface CreatorHit {
  uid: string;
  username: string | null;
  groupName: string | null;
  diamonds: number;
}

export interface Segment {
  key: string;
  start: string;
  end: string;
  full: boolean;
  source: SegmentSource;
  dayFiles: number;
  coveredDays: number;
  missingDays: string[];
  unsplittableDays: string[];
  futureDays: number;
}

export interface TokenResult {
  input: string;
  status: TokenStatus;
  uid?: string;
  username?: string | null;
  note?: string;
  /** NOT_FOUND: TikTok ID gần đúng ("Có phải …?"). */
  suggest?: string;
}

export interface HistoryItem {
  id: number;
  label: string;
  totalKc: number;
  creators: number;
  updatedAt: string;
  body: {
    ids: string;
    emails: string[];
    groups: string[];
    mode: LookupMode;
    compare: CompareMode;
    months?: string[];
    from?: string;
    to?: string;
  } | null;
}

export interface LookupRow {
  uid: string;
  username: string | null;
  email: string | null;
  emails: string[];
  groupName: string | null;
  userId: number | null;
  fromList: boolean;
  fromEmail: boolean;
  renamedFrom: string | null;
  diamonds: number;
  bySegment: Record<string, number>;
  liveSeconds: number;
  liveDays: number;
  pkDiamonds: number;
  newFollowers: number;
  daily: Record<string, number>;
  compareDiamonds: number | null;
  deltaPct: number | null;
  emailChanged: boolean;
  departedAt: string | null;
}

export interface Totals {
  diamonds: number;
  liveSeconds: number;
  liveDays: number;
  pkDiamonds: number;
  newFollowers: number;
  creators: number;
  creatorsWithKc: number;
  avgPerCreator: number;
  top10Share: number;
  under10k: number;
  compareDiamonds: number | null;
  deltaPct: number | null;
}

export interface LookupResult {
  today: string;
  mode: LookupMode;
  emails: string[];
  groups: string[];
  segments: Segment[];
  compare: {
    mode: CompareMode;
    key: string;
    start: string;
    end: string;
    available: boolean;
    note: string | null;
  } | null;
  totals: Totals;
  tokens: TokenResult[];
  tokenCounts: Partial<Record<TokenStatus, number>>;
  scope: 'ALL' | 'GROUPS';
  review: {
    outOfScope: number;
    notFound: number;
    invalid: number;
    duplicate: number;
    noData: number;
    otherEmail: number;
    renamed: number;
    emailChanged: number;
    departed: number;
    missingDays: number;
    unsplittableDays: number;
  };
  rows: LookupRow[];
  daily: { date: string; diamonds: number }[];
}

export interface LookupBody {
  ids: string[];
  emails: string[];
  groups: string[];
  mode: LookupMode;
  months?: string[];
  from?: string;
  to?: string;
  compare: CompareMode;
}

export interface ExtractColumn {
  index: number;
  header: string;
  values: string[];
  score: number;
  uidLike: number;
  nameLike: number;
}

export interface AccessInfo {
  canUse: boolean;
  /** ALL: xem tất cả · GROUPS: Quản lý Studio / Agency, chỉ Creator nhóm mình. */
  scope: 'ALL' | 'GROUPS' | null;
  groups: string[];
}

export interface SavedListMeta {
  id: number;
  name: string;
  ownerId: number;
  ownerName: string | null;
  mine: boolean;
  canEdit: boolean;
  shared: boolean;
  count: number;
  emails: string[];
  groups: string[];
  mode: LookupMode;
  updatedAt: string;
}
export interface SavedListFull extends SavedListMeta {
  ids: string;
}
export interface SavedListBody {
  name?: string;
  ids?: string;
  emails?: string[];
  groups?: string[];
  mode?: LookupMode;
  shared?: boolean;
}

const data = async <T>(p: Promise<any>): Promise<T> => (await p)?.data as T;

export const kcLookupApi = {
  access: () => data<AccessInfo>(AxiosUtils.get(API.kcLookupAccess)),
  history: () => data<{ items: HistoryItem[] }>(AxiosUtils.get(API.kcLookupHistory)),
  removeHistory: (id?: number) =>
    AxiosUtils._delete(id ? `${API.kcLookupHistory}/${id}` : API.kcLookupHistory, undefined),
  lists: () => data<{ lists: SavedListMeta[] }>(AxiosUtils.get(API.kcLookupLists)),
  getList: (id: number) => data<SavedListFull>(AxiosUtils.get(`${API.kcLookupLists}/${id}`)),
  createList: (body: SavedListBody) => data<SavedListFull>(AxiosUtils.post(API.kcLookupLists, body)),
  updateList: (id: number, body: SavedListBody) =>
    data<SavedListFull>(AxiosUtils.put(`${API.kcLookupLists}/${id}`, body)),
  removeList: (id: number) => AxiosUtils._delete(`${API.kcLookupLists}/${id}`, undefined),
  months: () => data<{ today: string; months: MonthInfo[] }>(AxiosUtils.get(API.kcLookupMonths, { count: 12 })),
  agents: (period: { months?: string[]; from?: string; to?: string }) =>
    data<{ agents: AgentInfo[] }>(
      AxiosUtils.get(
        API.kcLookupAgents,
        period.months ? { months: period.months.join(',') } : { from: period.from, to: period.to }
      )
    ),
  groups: (period: { months?: string[]; from?: string; to?: string }) =>
    data<{ groups: GroupInfo[] }>(
      AxiosUtils.get(
        API.kcLookupGroups,
        period.months ? { months: period.months.join(',') } : { from: period.from, to: period.to }
      )
    ),
  search: (q: string, period: { months?: string[]; from?: string; to?: string }) =>
    data<{ creators: CreatorHit[] }>(
      AxiosUtils.get(
        API.kcLookupSearch,
        period.months ? { q, months: period.months.join(',') } : { q, from: period.from, to: period.to }
      )
    ),
  query: (body: LookupBody) => data<LookupResult>(AxiosUtils.post(API.kcLookupQuery, body)),
  extract: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return data<{ columns: ExtractColumn[] }>(AxiosUtils.post(API.kcLookupExtract, form));
  },
  async exportXlsx(body: LookupBody) {
    const { token } = await data<{ token: string }>(AxiosUtils.post(API.kcLookupExport, body));
    const response: any = await AxiosUtils.get(`${API.kcLookupExport}/${token}`, undefined, undefined, {
      responseType: 'blob',
      timeout: 120_000,
    });
    const filename = parseFilename(response?.headers?.['content-disposition'], 'tra-cuu-kc.xlsx');
    triggerDownload(
      new Blob([response.data], {
        type:
          response?.headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      filename
    );
  },
};

/** Lời lỗi từ 400/409 (AxiosUtils ném nguyên lỗi axios). */
export function errorText(err: any): string {
  const d = err?.response?.data ?? err;
  const m = d?.message;
  if (Array.isArray(m)) return m.join('; ');
  return typeof m === 'string' && m ? m : 'Không tra được — thử lại sau.';
}
